import { useLazyGetPropertyListQuery } from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { useEffect, useMemo, useState } from "react";
import {
  CPropertyListFilter,
  IPropertyListFilterSchema,
  PropertyListFilterSchema
} from "./util/propertyListFilter";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MxListObservableChange from "@rf-l1/page/list/MxListObservableChange";
import MxListLayoutMain from "@rf-l1/page/list/MxListLayoutMain";
import MxListLayoutBody from "@rf-l1/page/list/MxListLayoutBody";
import convertPropertyListAdapter from "../../adapters/propertyListFiltersAdapters";
import PropertyMainFilters from "./components/PropertyMainFilters";
import PropertyList from "./components/PropertyListComponent";
import SharePropertyProvider from "../../context/SharePropertyProvider.context";
import { Collapse, Grid } from "@mui/material";
import useProfileCompleteEvaluation from "./hooks/useProfileCompleteEvaluation";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";
import usePropertyLoadActionsFromQuery from "../../hooks/usePropertyLoadActionsFromQuery";
import { debounce } from "lodash";
import HCheckArray from "@rf-helper/check-have-value";
import { PropertyListAdapter } from "../../adapters/list-property-adapter";
import { useLoggedUser } from "@/shared/store/auth";

function PropertyListPage() {
  const {} = usePropertyLoadActionsFromQuery();
  const [GET_PROPERTIES, GET_PROPERTIES_DATA] = useLazyGetPropertyListQuery();
  const { CheckProfileComplete } = useProfileCompleteEvaluation();
  const { userLogged } = useLoggedUser();
  const form = useForm({
    defaultValues: {
      ...CPropertyListFilter
    },
    mode: "onChange",
    resolver: yupResolver(PropertyListFilterSchema)
  });

  const handleUpdateFilters = useMemo(
    () =>
      debounce((data: IPropertyListFilterSchema) => {
        GET_PROPERTIES({
          query: convertPropertyListAdapter(data)
        })
          .unwrap()
          .then(() => {
            const element = document.getElementById("scroll-to-top");
            if (element) {
              element.scrollIntoView();
            }
          })
          .catch(e => HToastCatchServerError(e));
      }, 500),
    [GET_PROPERTIES]
  );

  useEffect(() => {
    if (!userLogged?.token || !userLogged?.maxCoreTokenV1) {
      return;
    }

    CheckProfileComplete();
    handleUpdateFilters(form.getValues());

    return () => {
      CheckProfileComplete.cancel();
      handleUpdateFilters.cancel();
    };
  }, [userLogged?.token, userLogged?.maxCoreTokenV1]);

  const [mapResetKey, setMapResetKey] = useState(0);
  const totalProperties = GET_PROPERTIES_DATA?.data?.meta?.total || 0;

  const PROPERTIES = useMemo(() => {
    if (HCheckArray(GET_PROPERTIES_DATA?.data?.data)) {
      return PropertyListAdapter(GET_PROPERTIES_DATA?.data) || [];
    } else {
      return [];
    }
  }, [GET_PROPERTIES_DATA?.data]);

  const showPagination = useMemo(() => {
    if (
      GET_PROPERTIES_DATA.status == "pending" ||
      GET_PROPERTIES_DATA.isUninitialized
    ) {
      return false;
    }
    if (GET_PROPERTIES_DATA.isError) {
      return false;
    }
    if (GET_PROPERTIES_DATA.data?.data?.length == 0) {
      return false;
    }
    if (!GET_PROPERTIES_DATA?.data) {
      return false;
    }

    return true;
  }, [
    GET_PROPERTIES_DATA.status,
    GET_PROPERTIES_DATA.isUninitialized,
    GET_PROPERTIES_DATA?.data,
    GET_PROPERTIES_DATA.isError,
    GET_PROPERTIES_DATA.data?.data?.length
  ]);

  return (
    <SharePropertyProvider>
      <FormProvider {...form}>
        <MxListObservableChange handleOnChangeFilter={handleUpdateFilters} />
        <MxListLayoutMain
          sx={{
            display: "grid",
            minHeight: "var(--content-available)",
            gridTemplateRows: "auto auto 1fr"
          }}
        >
          <Grid container item xs={12} id="scroll-to-top">
            {/* <NewsBar {...NewsBarExample} /> */}
          </Grid>

          <PropertyMainFilters
            total={totalProperties}
            onResetMap={() => setMapResetKey(prev => prev + 1)}
          />

          <PropertyList
            data={PROPERTIES}
            total={totalProperties}
            mapResetKey={mapResetKey}
            pagination={
              showPagination
                ? {
                    page: GET_PROPERTIES_DATA?.data?.meta?.current_page || 1,
                    totalPages: GET_PROPERTIES_DATA?.data?.meta?.last_page || 1,
                    onChangePage: page => {
                      handleUpdateFilters({
                        ...form.getValues(),
                        page
                      });
                    }
                  }
                : undefined
            }
          />
        </MxListLayoutMain>
      </FormProvider>
    </SharePropertyProvider>
  );
}

export default PropertyListPage;
